import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import api, { getToken } from '../../lib/api';
import {
    Search, MapPin, Users, Star, Trophy, Shield, ArrowRight,
    Sparkles, TrendingUp, Award, CheckCircle, ChevronRight, Building2
} from 'lucide-react';

// Animation variants
const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
};

const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } }
};

// Logo Component
function HomlyLogo({ size = 'md' }) {
    const sizes = {
        sm: 'text-xl',
        md: 'text-2xl',
        lg: 'text-3xl',
        xl: 'text-4xl'
    };
    
    return (
        <div className={`font-black ${sizes[size]} flex items-center gap-0.5`}>
            <span className="bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">
                Homly
            </span>
            <span className="text-[#FF914D]">.</span>
        </div>
    );
}

// Animated gradient orb
function GradientOrb({ className }) {
    return (
        <motion.div
            className={`absolute rounded-full blur-3xl ${className}`}
            animate={{
                scale: [1, 1.2, 1],
                opacity: [0.15, 0.25, 0.15],
            }}
            transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
            }}
        />
    );
}

// League Badge
function LeagueBadge({ league, size = 'md' }) {
    const leagueConfig = {
        bronze: { label: 'Bronze', color: '#CD7F32', bg: 'from-amber-700 to-amber-500' },
        silver: { label: 'Argent', color: '#C0C0C0', bg: 'from-gray-400 to-gray-300' },
        gold: { label: 'Or', color: '#FFD700', bg: 'from-yellow-500 to-yellow-300' },
        platinum: { label: 'Platine', color: '#E5E4E2', bg: 'from-slate-400 to-slate-200' },
        diamond: { label: 'Diamant', color: '#B9F2FF', bg: 'from-cyan-400 to-cyan-200' }
    };
    
    const config = leagueConfig[league] || leagueConfig.bronze;
    const sizeClass = size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
    
    return (
        <div className={`${sizeClass} rounded-lg bg-gradient-to-br ${config.bg} flex items-center justify-center`}>
            <Trophy className={size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'} style={{ color: '#fff' }} />
        </div>
    );
}

// Agent Card for featured agents
function AgentCard({ agent }) {
    const initials = agent.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?';
    const hasPhoto = agent.photo_url;
    
    return (
        <motion.div 
            whileHover={{ y: -4, transition: { duration: 0.2 } }}
            className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden hover:border-[#FF914D]/30 transition-all group"
        >
            {/* Header gradient */}
            <div className="h-2 bg-gradient-to-r from-[#681793] to-[#FF914D]" />
            
            <div className="p-5">
                <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center text-white font-bold text-xl flex-shrink-0 shadow-lg overflow-hidden">
                        {hasPhoto ? (
                            <img src={`/api/users/photo/${agent.photo_url}`} alt="" className="w-full h-full object-cover" />
                        ) : initials}
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-white truncate">{agent.full_name}</h3>
                        <p className="text-sm text-white/50 truncate">{agent.agency_name}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                            {agent.city && (
                                <span className="text-xs text-white/40 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> {agent.city}
                                </span>
                            )}
                            {agent.distance_km && (
                                <span className="text-xs text-[#FF914D] font-medium">{agent.distance_km} km</span>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Bio */}
                {agent.bio && (
                    <p className="text-xs text-white/50 mb-3 line-clamp-2">{agent.bio}</p>
                )}
                
                {/* Stats row */}
                <div className="flex items-center justify-between mb-3 p-2.5 bg-white/5 rounded-xl">
                    <div className="flex items-center gap-2">
                        <LeagueBadge league={agent.league} />
                        <div>
                            <p className="text-sm font-bold text-white">{Math.round(agent.elo)}</p>
                            <p className="text-[10px] text-white/40">HomlyScore</p>
                        </div>
                    </div>
                    <div className="text-center">
                        <p className="text-sm font-bold text-[#FF914D]">{agent.certifications_count || 0}</p>
                        <p className="text-[10px] text-white/40">Certifs</p>
                    </div>
                    <div className="text-right">
                        <div className="flex items-center gap-1 justify-end">
                            <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
                            <span className="text-sm font-bold text-white">{agent.avg_rating?.toFixed(1) || '—'}</span>
                        </div>
                        <p className="text-[10px] text-white/40">{agent.total_ratings} avis</p>
                    </div>
                </div>
                
                {/* Specialties */}
                {agent.specialties?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                        {agent.specialties.slice(0, 3).map((s, i) => (
                            <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[#681793]/20 text-[#FF914D]">{s}</span>
                        ))}
                        {agent.specialties.length > 3 && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-white/40">+{agent.specialties.length - 3}</span>
                        )}
                    </div>
                )}
                
                <Link to={`/p/agent/${agent.id}`}>
                    <Button 
                        variant="outline" 
                        className="w-full border-white/10 text-white/70 hover:text-white hover:bg-white/5 group-hover:border-[#FF914D]/50"
                    >
                        Voir le profil
                        <ChevronRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Button>
                </Link>
            </div>
        </motion.div>
    );
}

export default function ReseauLandingPage() {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [featuredAgents, setFeaturedAgents] = useState([]);
    const [featuredAgencies, setFeaturedAgencies] = useState([]);
    const [featuredTab, setFeaturedTab] = useState('agents');
    const [loading, setLoading] = useState(true);

    // Si un professionnel connecté arrive sur la landing publique, on le
    // redirige vers son cockpit personnel (rétention prioritaire).
    useEffect(() => {
        if (!getToken()) return;
        api.get('/auth/me')
            .then(r => {
                const role = r?.data?.role;
                if (["agent", "admin", "super_admin", "internal", "group_manager"].includes(role)) {
                    navigate('/reseau/cockpit', { replace: true });
                }
            })
            .catch(() => { /* not logged or invalid token — stay on public */ });
    }, [navigate]);

    useEffect(() => {
        loadFeaturedAgents();
    }, []);
    
    const loadFeaturedAgents = async () => {
        try {
            const [agentsRes, agenciesRes] = await Promise.all([
                api.get('/reseau/agents/featured?limit=6'),
                api.get('/reseau/agencies/featured?limit=6').catch(() => ({ data: { agencies: [] } }))
            ]);
            setFeaturedAgents(agentsRes.data.agents || []);
            setFeaturedAgencies(agenciesRes.data.agencies || []);
        } catch (error) {
            console.error('Error loading featured agents:', error);
        } finally {
            setLoading(false);
        }
    };
    
    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/reseau/recherche?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };
    
    return (
        <div style={{ background: '#0B0710' }} className="min-h-screen text-white overflow-hidden">
            {/* Background effects */}
            <GradientOrb className="w-[600px] h-[600px] bg-[#681793] -top-40 -left-40" />
            <GradientOrb className="w-[500px] h-[500px] bg-[#FF914D] top-1/2 -right-40" />
            
            {/* Grid pattern */}
            <div className="absolute inset-0 bg-[linear-gradient(rgba(104,23,147,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(104,23,147,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
            
            {/* Navbar */}
            <nav className="relative z-50 bg-[#0B0710]/80 backdrop-blur-xl border-b border-white/5">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-3">
                            <HomlyLogo size="md" />
                            <span className="text-sm font-medium text-white/50">Réseau</span>
                        </Link>
                        <div className="flex items-center gap-4">
                            <Link to="/reseau/recherche">
                                <Button variant="ghost" className="text-white/60 hover:text-white">
                                    <Search className="w-4 h-4 mr-2" />
                                    Rechercher
                                </Button>
                            </Link>
                            <Link to="/login">
                                <Button className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white">
                                    Connexion
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </nav>
            
            {/* Hero Section */}
            <section className="relative z-10 pt-20 pb-32">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div 
                        className="text-center max-w-4xl mx-auto"
                        initial="hidden"
                        animate="visible"
                        variants={stagger}
                    >
                        <motion.div variants={fadeIn} className="mb-6">
                            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/70">
                                <Sparkles className="w-4 h-4 text-[#FF914D]" />
                                Le réseau de confiance de l'immobilier
                            </span>
                        </motion.div>
                        
                        <motion.h1 
                            variants={fadeIn}
                            className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6"
                        >
                            Trouvez l'agent
                            <span className="block bg-gradient-to-r from-[#681793] to-[#FF914D] bg-clip-text text-transparent">
                                qui vous correspond
                            </span>
                        </motion.h1>
                        
                        <motion.p 
                            variants={fadeIn}
                            className="text-lg sm:text-xl text-white/60 mb-10 max-w-2xl mx-auto"
                        >
                            Comparez les agents immobiliers de votre zone grâce aux avis vérifiés 
                            de vrais vendeurs. Des profils transparents pour un choix éclairé.
                        </motion.p>
                        
                        {/* Search Box */}
                        <motion.form 
                            variants={fadeIn}
                            onSubmit={handleSearch}
                            className="max-w-xl mx-auto mb-8"
                        >
                            <div className="flex gap-3 p-2 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
                                <div className="flex-1 relative">
                                    <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                    <Input
                                        type="text"
                                        placeholder="Ville, code postal ou département..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-12 pr-4 py-3 bg-transparent border-0 text-white placeholder:text-white/40 focus-visible:ring-0"
                                    />
                                </div>
                                <Button 
                                    type="submit"
                                    className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white px-6"
                                >
                                    <Search className="w-4 h-4 mr-2" />
                                    Rechercher
                                </Button>
                            </div>
                        </motion.form>
                        
                        {/* Trust indicators */}
                        <motion.div 
                            variants={fadeIn}
                            className="flex flex-wrap items-center justify-center gap-6 text-sm text-white/50"
                        >
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-400" />
                                <span>Avis vérifiés</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Shield className="w-4 h-4 text-blue-400" />
                                <span>Données sécurisées</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Users className="w-4 h-4 text-[#FF914D]" />
                                <span>Agents certifiés</span>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>
            
            {/* Featured Agents Section */}
            <section className="relative z-10 py-20 bg-white/[0.02]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={stagger}
                    >
                        <motion.div variants={fadeIn} className="text-center mb-8">
                            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
                                Les meilleurs professionnels
                            </h2>
                            <p className="text-white/50 mb-6">
                                Découvrez les professionnels qui excellent dans leur métier
                            </p>
                            <div className="inline-flex bg-white/5 rounded-xl p-1 border border-white/10">
                                <button onClick={() => setFeaturedTab('agents')} className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${featuredTab === 'agents' ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white' : 'text-white/50 hover:text-white'}`}>
                                    Meilleurs Agents
                                </button>
                                <button onClick={() => setFeaturedTab('agencies')} className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${featuredTab === 'agencies' ? 'bg-gradient-to-r from-[#681793] to-[#FF914D] text-white' : 'text-white/50 hover:text-white'}`}>
                                    Meilleures Agences
                                </button>
                            </div>
                        </motion.div>
                        
                        {loading ? (
                            <div className="flex justify-center py-12">
                                <div className="w-8 h-8 border-2 border-[#681793] border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : featuredTab === 'agents' ? (
                            featuredAgents.length > 0 ? (
                                <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {featuredAgents.map((agent) => (
                                        <motion.div key={agent.id} variants={fadeIn}>
                                            <AgentCard agent={agent} />
                                        </motion.div>
                                    ))}
                                </motion.div>
                            ) : (
                                <div className="text-center py-12">
                                    <Users className="w-12 h-12 text-white/10 mx-auto mb-3" />
                                    <p className="text-white/50">Aucun agent disponible pour le moment</p>
                                </div>
                            )
                        ) : (
                            featuredAgencies.length > 0 ? (
                                <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {featuredAgencies.map((agency) => (
                                        <motion.div key={agency.id} variants={fadeIn}>
                                            <Link to={`/p/agence/${agency.id}`}>
                                                <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden hover:border-[#FF914D]/30 transition-all group">
                                                    <div className="h-2" style={{ background: `linear-gradient(90deg, ${agency.primary_color || '#681793'}, #FF914D)` }} />
                                                    <div className="p-5">
                                                        <div className="flex items-center gap-4 mb-3">
                                                            <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 border border-white/10 overflow-hidden" style={{ background: `${agency.primary_color || '#681793'}20` }}>
                                                                {agency.logo_storage_path ? (
                                                                    <img src={`/api/users/photo/${agency.logo_storage_path}`} alt="" className="w-full h-full object-cover" />
                                                                ) : (
                                                                    <Building2 className="w-7 h-7" style={{ color: agency.primary_color || '#681793' }} />
                                                                )}
                                                            </div>
                                                            <div>
                                                                <h3 className="font-bold text-white group-hover:text-[#FF914D] transition-colors">{agency.name}</h3>
                                                                {agency.city && <p className="text-xs text-white/40 flex items-center gap-1"><MapPin className="w-3 h-3" />{agency.city}</p>}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center justify-between p-2.5 bg-white/5 rounded-xl">
                                                            <div className="text-center">
                                                                <p className="text-sm font-bold text-white">{agency.total_agents}</p>
                                                                <p className="text-[10px] text-white/40">Agents</p>
                                                            </div>
                                                            <div className="text-center">
                                                                <p className="text-sm font-bold text-[#FF914D]">{agency.avg_elo}</p>
                                                                <p className="text-[10px] text-white/40">HomlyScore moyen</p>
                                                            </div>
                                                            <div className="text-center">
                                                                <p className="text-sm font-bold text-white capitalize">{agency.league}</p>
                                                                <p className="text-[10px] text-white/40">Ligue</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Link>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            ) : (
                                <div className="text-center py-12">
                                    <Building2 className="w-12 h-12 text-white/10 mx-auto mb-3" />
                                    <p className="text-white/50">Aucune agence disponible pour le moment</p>
                                </div>
                            )
                        )}
                        
                        <motion.div variants={fadeIn} className="text-center mt-10">
                            <Link to="/reseau/recherche">
                                <Button 
                                    size="lg"
                                    className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white"
                                >
                                    Voir tous les agents
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </section>
            
            {/* How it works */}
            <section className="relative z-10 py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={stagger}
                    >
                        <motion.div variants={fadeIn} className="text-center mb-12">
                            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
                                Comment ça marche ?
                            </h2>
                            <p className="text-white/50">
                                Un processus simple pour trouver votre agent idéal
                            </p>
                        </motion.div>
                        
                        <motion.div variants={stagger} className="grid md:grid-cols-3 gap-8">
                            {[
                                {
                                    step: '1',
                                    title: 'Recherchez',
                                    description: 'Entrez votre ville ou code postal pour découvrir les agents de votre zone.',
                                    icon: Search,
                                    color: 'from-[#681793] to-[#9b4dca]'
                                },
                                {
                                    step: '2',
                                    title: 'Comparez',
                                    description: 'Consultez les profils, notes, avis et spécialités de chaque agent.',
                                    icon: TrendingUp,
                                    color: 'from-[#FF914D] to-[#ffb380]'
                                },
                                {
                                    step: '3',
                                    title: 'Choisissez',
                                    description: 'Contactez directement l\'agent qui vous correspond et démarrez votre projet.',
                                    icon: CheckCircle,
                                    color: 'from-green-500 to-emerald-400'
                                }
                            ].map((item, idx) => (
                                <motion.div 
                                    key={idx}
                                    variants={fadeIn}
                                    className="relative bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-8 text-center hover:border-white/20 transition-all"
                                >
                                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mx-auto mb-6`}>
                                        <item.icon className="w-8 h-8 text-white" />
                                    </div>
                                    <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-sm font-bold text-white/40">
                                        {item.step}
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                                    <p className="text-white/50">{item.description}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </motion.div>
                </div>
            </section>
            
            {/* Trust Section */}
            <section className="relative z-10 py-20 bg-white/[0.02]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={stagger}
                        className="text-center"
                    >
                        <motion.div variants={fadeIn} className="mb-12">
                            <h2 className="text-3xl sm:text-4xl font-black text-white mb-4">
                                Un système de confiance unique
                            </h2>
                            <p className="text-white/50 max-w-2xl mx-auto">
                                Chaque note, chaque avis provient d'un vrai vendeur ayant signé un mandat. 
                                Pas de faux avis, pas de manipulation.
                            </p>
                        </motion.div>
                        
                        <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {[
                                { icon: Shield, label: 'Avis vérifiés', desc: 'Liés à des mandats réels' },
                                { icon: Users, label: 'Vendeurs anonymisés', desc: 'Confidentialité garantie' },
                                { icon: Trophy, label: 'Classement HomlyScore', desc: 'Score de performance fiable' },
                                { icon: Award, label: 'Badges mérités', desc: 'Récompenses objectives' }
                            ].map((item, idx) => (
                                <motion.div 
                                    key={idx}
                                    variants={fadeIn}
                                    className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center mx-auto mb-4">
                                        <item.icon className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="font-bold text-white mb-1">{item.label}</h3>
                                    <p className="text-sm text-white/50">{item.desc}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </motion.div>
                </div>
            </section>
            
            {/* CTA */}
            <section className="relative z-10 py-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeIn}
                    >
                        <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">
                            Prêt à trouver votre agent ?
                        </h2>
                        <p className="text-white/50 mb-8">
                            Lancez votre recherche et découvrez les meilleurs professionnels de votre zone.
                        </p>
                        <Link to="/reseau/recherche">
                            <Button 
                                size="lg"
                                className="bg-gradient-to-r from-[#681793] to-[#FF914D] hover:opacity-90 text-white shadow-lg shadow-[#681793]/30"
                            >
                                <Search className="w-5 h-5 mr-2" />
                                Rechercher un agent
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </section>
            
            {/* Footer */}
            <footer className="relative z-10 bg-[#0B0710] border-t border-white/5 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <Link to="/" className="flex items-center gap-2">
                            <HomlyLogo size="sm" />
                            <span className="text-sm text-white/40">Réseau</span>
                        </Link>
                        <div className="flex items-center gap-6 text-sm text-white/40">
                            <Link to="/cgu" className="hover:text-white transition-colors">CGU</Link>
                            <Link to="/privacy" className="hover:text-white transition-colors">Confidentialité</Link>
                            <Link to="/" className="hover:text-white transition-colors">Retour au site</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
